/**
 * The quote page's master tabs (design 27): the job page's shape (D-083) over
 * a quote's tabs. Every master is one tab of its own since design 46 — General
 * lost its Notes and Details sub tabs to one page, the Fields card above the
 * notes — in Dakota's order of 2026-10-12 (D-147): General, To-dos, Quote
 * worksheet, Line items, Files. The URL carries the master —
 * `/quotes/24342/worksheet`; the older two-segment addresses redirect. The API
 * serves the lines and the files by key (`QUOTE_TABS` in shared); General's
 * reads, the worksheet and the to-dos have endpoints of their own.
 */

import type { QuoteTab } from '@unitedos/shared';
import { QUOTE_TABS } from '@unitedos/shared';

export const QUOTE_MASTERS = ['general', 'to-dos', 'worksheet', 'line-items', 'documents'] as const;
export type QuoteMaster = (typeof QUOTE_MASTERS)[number];

export const QUOTE_MASTER_LABELS: Readonly<Record<QuoteMaster, string>> = {
  general: 'General',
  'to-dos': 'To-dos',
  worksheet: 'Quote worksheet',
  'line-items': 'Line items',
  documents: 'Files',
};

export const QUOTE_TAB_LABELS: Readonly<Record<QuoteTab, string>> = {
  general: 'General',
  'to-dos': 'To-dos',
  'line-items': 'Line items',
  worksheet: 'Quote worksheet',
  documents: 'Files',
};

/** Each master's tabs, in the order the row shows them. A master with one tab named as itself has no sub-tab row. */
const MASTER_TABS: Readonly<Record<QuoteMaster, readonly QuoteTab[]>> = {
  // One page since design 46 (2026-11-02): the cards, the Fields card, the notes.
  general: ['general'],
  'to-dos': ['to-dos'],
  worksheet: ['worksheet'],
  'line-items': ['line-items'],
  documents: ['documents'],
};

/** Tab keys the page answered to before, each mapped to today's key: the Checklist tab became To-dos (design 39), General's two sub tabs General itself (design 46). */
const LEGACY_TABS: Readonly<Record<string, QuoteTab>> = { checklist: 'to-dos', communications: 'general', details: 'general' };

export function isQuoteMaster(value: string | undefined): value is QuoteMaster {
  return value !== undefined && (QUOTE_MASTERS as readonly string[]).includes(value);
}

export function isQuoteTab(value: string | undefined): value is QuoteTab {
  return value !== undefined && (QUOTE_TABS as readonly string[]).includes(value);
}

export function quoteTabsOf(master: QuoteMaster): readonly QuoteTab[] {
  return MASTER_TABS[master];
}

/** A master that is one tab of its own: no sub-tab row, a one-segment address. */
export function isSoloQuoteMaster(master: QuoteMaster): boolean {
  const tabs = MASTER_TABS[master];
  return tabs.length === 1 && tabs[0] === master;
}

export function firstQuoteTabOf(master: QuoteMaster): QuoteTab {
  const first = MASTER_TABS[master][0];
  if (first === undefined) throw new Error(`quote master ${master} has no tabs`);
  return first;
}

export function quoteMasterOf(tab: QuoteTab): QuoteMaster {
  const master = QUOTE_MASTERS.find((m) => MASTER_TABS[m].includes(tab));
  if (master === undefined) throw new Error(`quote tab ${tab} belongs to no master`);
  return master;
}

/** The address of a tab: `/quotes/N/master/tab`, or `/quotes/N/master` for a master that is its own one tab. */
export function quoteHref(quoteNumber: string, master: QuoteMaster, tab: QuoteTab = firstQuoteTabOf(master)): string {
  return isSoloQuoteMaster(master) ? `/quotes/${quoteNumber}/${master}` : `/quotes/${quoteNumber}/${master}/${tab}`;
}

export type QuoteAddress = { readonly kind: 'page'; readonly master: QuoteMaster; readonly tab: QuoteTab } | { readonly kind: 'redirect'; readonly to: string } | { readonly kind: 'missing' };

/** A quote's address is its number as digits (every quote on dev, 2026-10-10); anything else is the missing page. */
export function isQuoteNumber(value: string | undefined): value is string {
  return value !== undefined && /^[0-9]{1,20}$/.test(value);
}

/**
 * What an address means: `/quotes/N` → General's first tab (a redirect); `/quotes/N/x` → the page
 * for a solo master, a redirect for a master with sub tabs or a bare tab key; `/quotes/N/m/t` →
 * the page when the table holds the pair, else a redirect to where the tab lives; anything else
 * is the missing page.
 */
export function resolveQuoteAddress(quoteNumber: string | undefined, first: string | undefined, second: string | undefined): QuoteAddress {
  if (!isQuoteNumber(quoteNumber)) return { kind: 'missing' };
  if (first === undefined) return { kind: 'redirect', to: quoteHref(quoteNumber, 'general') };
  if (second === undefined) {
    if (isQuoteMaster(first) && isSoloQuoteMaster(first)) return { kind: 'page', master: first, tab: firstQuoteTabOf(first) };
    if (isQuoteMaster(first)) return { kind: 'redirect', to: quoteHref(quoteNumber, first) };
    const key = LEGACY_TABS[first] ?? first;
    if (!isQuoteTab(key)) return { kind: 'missing' };
    return { kind: 'redirect', to: quoteHref(quoteNumber, quoteMasterOf(key), key) };
  }
  const tab = LEGACY_TABS[second] ?? second;
  if (!isQuoteMaster(first) || !isQuoteTab(tab)) return { kind: 'missing' };
  // `/quotes/N/general/details` and the rest: every master is one tab now, so a second segment always redirects.
  if (MASTER_TABS[first].includes(tab) && !isSoloQuoteMaster(first) && tab === second) return { kind: 'page', master: first, tab };
  return { kind: 'redirect', to: quoteHref(quoteNumber, quoteMasterOf(tab), tab) };
}

/** A quote draft's address (design 44): `draft-12`, which the quote routes take in place of a number. */
export function isDraftRef(value: string | undefined): value is string {
  return value !== undefined && /^draft-[1-9][0-9]{0,17}$/.test(value);
}

/** The draft's id from its ref: `draft-12` → 12. */
export function draftIdOf(ref: string): number {
  return Number(ref.slice('draft-'.length));
}

/** A draft's tab: `/quotes/draft-12/master` — each master its own one tab, as a quote's are. */
export function draftHref(ref: string, master: QuoteMaster): string {
  return `/quotes/${ref}/${master}`;
}

export type DraftAddress = { readonly kind: 'page'; readonly master: QuoteMaster } | { readonly kind: 'redirect'; readonly to: string } | { readonly kind: 'missing' };

/** What a draft's address means: `/quotes/draft-12` → General; a master → its page; a Jobber quote's tab key → the master it lives under; anything else is the missing page. */
export function resolveDraftAddress(ref: string | undefined, first: string | undefined, second: string | undefined): DraftAddress {
  if (!isDraftRef(ref)) return { kind: 'missing' };
  if (first === undefined) return { kind: 'redirect', to: draftHref(ref, 'general') };
  const key = LEGACY_TABS[first] ?? first;
  if (isQuoteMaster(key)) return second === undefined && key === first ? { kind: 'page', master: key } : { kind: 'redirect', to: draftHref(ref, key) };
  if (isQuoteTab(key)) return { kind: 'redirect', to: draftHref(ref, quoteMasterOf(key)) };
  return { kind: 'missing' };
}
